import React, { useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import {
  changePassword,
  setupTotp,
  enableTotp,
  disableTotp,
  listSessions,
  revokeSession,
  revokeOtherSessions,
  updateUserProfile,
} from "../../api/apiClient";
import { passwordViolation } from "./profileModel";

// Render these only while open; unmounting resets their form state.

const titleSx = { fontWeight: 700, color: "var(--color-ink)", fontFamily: "var(--font-display)" };
const cancelSx = { textTransform: "none", color: "var(--color-ink)" };
const primarySx = { textTransform: "none", bgcolor: "var(--color-primary)", "&:hover": { bgcolor: "var(--color-ink-deep)" } };
const errorMessage = (err, fallback) => err?.response?.data?.message || fallback;

export function PasswordDialog({ userId, onClose, onDone }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setError(null);
    if (!current) return setError("Current password is required.");
    const violation = passwordViolation(next);
    if (violation) return setError(violation);
    if (next !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      await changePassword(userId, current, next);
      onDone("Password updated successfully.");
    } catch (err) {
      setError(errorMessage(err, "Failed to update password."));
      setLoading(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={titleSx}>Update password</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Current password" type="password" fullWidth size="small" value={current} onChange={(e) => setCurrent(e.target.value)} autoFocus />
          <TextField label="New password" type="password" fullWidth size="small" value={next} onChange={(e) => setNext(e.target.value)} helperText="8+ characters with an uppercase letter, a number and a symbol" />
          <TextField label="Confirm new password" type="password" fullWidth size="small" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={cancelSx}>Cancel</Button>
        <Button variant="contained" disabled={loading} onClick={submit} sx={primarySx}>{loading ? "Saving…" : "Update password"}</Button>
      </DialogActions>
    </Dialog>
  );
}

export function EmailDialog({ userId, currentEmail, onClose, onSaved }) {
  const [email, setEmail] = useState("");
  const [confirm, setConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (!email.trim()) return setError("New email is required.");
    if (email.trim() !== confirm.trim()) return setError("Emails do not match.");
    if (!password) return setError("Enter your current password to confirm.");
    setSaving(true);
    try {
      const saved = await updateUserProfile(userId, { email: email.trim(), currentPassword: password });
      onSaved(saved?.email || email.trim());
    } catch (err) {
      const status = err?.response?.status;
      setError(status === 400 ? errorMessage(err, "Incorrect password.") : "Failed to update email. It may already be in use.");
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={titleSx}>Change email address</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Current email" fullWidth size="small" value={currentEmail || ""} disabled />
          <TextField label="New email address" type="email" fullWidth size="small" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
          <TextField label="Confirm new email" type="email" fullWidth size="small" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          <TextField label="Current password" type="password" fullWidth size="small" value={password} onChange={(e) => setPassword(e.target.value)} helperText="Required to change the email on your account" />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={cancelSx}>Cancel</Button>
        <Button variant="contained" disabled={saving} onClick={submit} sx={primarySx}>{saving ? "Saving…" : "Update email"}</Button>
      </DialogActions>
    </Dialog>
  );
}

export function TwoFactorDialog({ mfaEnabled, onClose, onChanged }) {
  const [phase, setPhase] = useState(mfaEnabled ? "disable" : "loading");
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (mfaEnabled) return undefined;
    let live = true;
    setupTotp()
      .then((data) => { if (live) { setSetup(data); setPhase("qr"); } })
      .catch(() => { if (live) setError("Failed to initialize 2FA setup. Try again."); });
    return () => { live = false; };
  }, [mfaEnabled]);

  const run = async (action, failure, successMessage, nextEnabled) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      onChanged(nextEnabled, successMessage);
    } catch (err) {
      setError(errorMessage(err, failure));
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={titleSx}>{mfaEnabled ? "Turn off two-step verification" : "Set up two-step verification"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {phase === "loading" && !error && <Box sx={{ py: 3, textAlign: "center" }}><CircularProgress size={28} /></Box>}
          {phase === "qr" && setup && (
            <>
              <Typography variant="body2">Scan the QR code with your authenticator app, then enter the 6-digit code to confirm.</Typography>
              <Box sx={{ display: "flex", justifyContent: "center", p: 2, bgcolor: "#fff", border: "1px solid var(--color-hairline-cloud)", borderRadius: "12px" }}>
                <QRCodeSVG value={setup.otpauthUri} size={180} />
              </Box>
              <Box sx={{ bgcolor: "#fafafb", border: "1px solid var(--color-hairline-cloud)", borderRadius: "8px", p: "10px 14px" }}>
                <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>Manual entry key</Typography>
                <Typography sx={{ fontFamily: "var(--font-mono)", fontSize: 13, fontWeight: 600, wordBreak: "break-all" }}>{setup.secret}</Typography>
              </Box>
            </>
          )}
          {phase === "verify" && (
            <>
              <Typography variant="body2">Enter the 6-digit code from your authenticator app to turn on two-step verification.</Typography>
              <TextField label="Authentication code" fullWidth size="small" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputProps={{ inputMode: "numeric", maxLength: 6 }} autoFocus />
              <Button variant="text" sx={{ ...cancelSx, alignSelf: "flex-start", p: 0 }} onClick={() => setPhase("qr")}>← Back to QR code</Button>
            </>
          )}
          {phase === "disable" && (
            <>
              <Alert severity="warning">Turning this off reduces your account security.</Alert>
              <TextField label="Current password" type="password" fullWidth size="small" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={cancelSx}>Cancel</Button>
        {phase === "qr" && <Button variant="contained" onClick={() => setPhase("verify")} sx={primarySx}>Next</Button>}
        {phase === "verify" && (
          <Button variant="contained" disabled={busy || code.length < 6} sx={primarySx}
            onClick={() => run(() => enableTotp(code), "Invalid code. Please try again.", "Two-factor authentication enabled.", true)}>
            {busy ? "Verifying…" : "Turn on"}
          </Button>
        )}
        {phase === "disable" && (
          <Button variant="outlined" color="error" disabled={busy || !password} sx={{ textTransform: "none" }}
            onClick={() => run(() => disableTotp(password), "Incorrect password.", "Two-factor authentication disabled.", false)}>
            {busy ? "Turning off…" : "Turn off"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export function SessionsDialog({ userId, onClose, onNotify }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    listSessions(userId)
      .then((list) => { if (live) setSessions(list); })
      .catch(() => {})
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [userId]);

  const signOut = async (id) => {
    try {
      await revokeSession(userId, id);
      setSessions((prev) => prev.filter((s) => s.id !== id));
    } catch {
      onNotify("Couldn't sign that device out. Please try again.", "error");
    }
  };

  const signOutOthers = async () => {
    try {
      await revokeOtherSessions(userId);
      setSessions(await listSessions(userId).catch(() => sessions));
      onNotify("All other sessions signed out.");
    } catch {
      onNotify("Couldn't sign out your other devices. Please try again.", "error");
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={titleSx}>Active sessions</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ py: 3, textAlign: "center" }}><CircularProgress size={28} /></Box>
        ) : sessions.length === 0 ? (
          <Typography sx={{ py: 2, textAlign: "center" }}>No active sessions found.</Typography>
        ) : (
          <div>
            {sessions.map((s, i) => {
              const current = Boolean(s.current);
              return (
                <div className="pf" key={s.id || i} style={{ background: "none", minHeight: 0 }}>
                  <div className="pf-lr" style={{ gridTemplateColumns: "minmax(0,1fr) auto" }}>
                    <div>
                      <b>{s.device || "Unknown device"}</b>
                      <small>{s.ipAddress || "IP unknown"} · Started {new Date(s.createdAt).toLocaleString()}</small>
                      <small>Last active {new Date(s.lastActiveAt).toLocaleString()}</small>
                    </div>
                    <div className="r">
                      {current ? <span className="pf-tag ok">This session</span> : (
                        <button type="button" className="pf-btn pf-btn-dg pf-btn-sm" onClick={() => signOut(s.id)}>Sign out</button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={cancelSx}>Close</Button>
        {sessions.length > 1 && <Button variant="outlined" color="error" sx={{ textTransform: "none" }} onClick={signOutOthers}>Sign out all other devices</Button>}
      </DialogActions>
    </Dialog>
  );
}
